import React, { useContext } from 'react';
import { AppContext } from '../App';
import { RefreshCw, Clock, Zap, MapPin, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const Header = () => {
  const { lastUpdated, refreshData, selectedProvince, setSelectedProvince, provinces } = useContext(AppContext);

  const formatLastUpdated = () => {
    if (!lastUpdated) return 'Never';
    const diff = Math.floor((new Date() - lastUpdated) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return lastUpdated.toLocaleTimeString();
  };

  const selectedProvinceName = selectedProvince 
    ? provinces.find(p => p.code === selectedProvince)?.name 
    : null;

  return (
    <header 
      className="sticky top-0 z-50 glass border-b border-[#27272A]"
      data-testid="header"
    >
      <div className="max-w-[1800px] mx-auto px-4 md:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">VehicleIQ</h1>
              <p className="text-xs text-[#A1A1AA]">
                {selectedProvinceName ? `${selectedProvinceName} Market` : 'Canadian Market Intelligence'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Province Selector */}
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#A1A1AA] hidden sm:block" />
              <Select 
                value={selectedProvince || "all"} 
                onValueChange={(val) => setSelectedProvince(val === "all" ? null : val)}
              >
                <SelectTrigger 
                  className="w-[140px] sm:w-[180px] bg-[#27272A] border-[#27272A] text-white"
                  data-testid="province-selector"
                >
                  <SelectValue placeholder="All Canada" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181B] border-[#27272A] max-h-[300px] z-[100]">
                  <SelectItem value="all" className="text-white focus:bg-[#27272A] focus:text-white">
                    <div className="flex items-center gap-2">
                      <span>🇨🇦</span>
                      <span>All Canada</span>
                    </div>
                  </SelectItem>
                  {provinces.map((province) => (
                    <SelectItem 
                      key={province.code} 
                      value={province.code}
                      className="text-white focus:bg-[#27272A] focus:text-white"
                    >
                      <div className="flex items-center justify-between w-full gap-2">
                        <span>{province.name}</span>
                        <span className="text-xs text-[#52525B]">{province.market_share}%</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedProvince && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedProvince(null)}
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5 h-8 w-8"
                  data-testid="clear-province"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Live indicator */}
            <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-[#22C55E]/10 rounded-full">
              <div className="status-dot" />
              <span className="text-xs text-[#22C55E] font-medium">Live</span>
            </div>

            {/* Last updated */}
            <div className="hidden md:flex items-center gap-2 text-[#A1A1AA]">
              <Clock className="w-4 h-4" />
              <span className="text-xs">{formatLastUpdated()}</span>
            </div>

            {/* Refresh button */}
            <Button 
              onClick={refreshData}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white btn-glow"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>

        {/* Province indicator banner */}
        {selectedProvince && (
          <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-[#3B82F6]/10 rounded-lg border border-[#3B82F6]/20">
            <MapPin className="w-4 h-4 text-[#3B82F6]" />
            <span className="text-sm text-[#3B82F6]">
              Viewing <strong>{selectedProvinceName}</strong> market data
            </span>
            <span className="text-xs text-[#A1A1AA] ml-2">
              ({provinces.find(p => p.code === selectedProvince)?.market_share}% of national market)
            </span>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
